import { inject, Injectable } from '@angular/core';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import { FilterHttpService } from '@wdx/shared/infrastructure/api-service';
import {
    FilterOperator,
    LookupFieldResult,
    LookupObject,
    LookupSourceType,
    Query,
} from '@wdx/shared/swagger-gen';
import { forkJoin, map, Observable, of } from 'rxjs';
import { InfoCardData } from '../../../models/info-card-data.model';

export type ContactPointLookup = {
    id?: string;
    name?: string;
    type?: LookupSourceType;
    party?: LookupFieldResult;
};

@Injectable({
    providedIn: 'root',
})
export class InfoCardService {
    private filterHttpService = inject(FilterHttpService);

    getLookups$(
        value:
            | LookupFieldResult
            | LookupObject
            | Array<LookupFieldResult | LookupObject>,
    ): Observable<LookupFieldResult[]> {
        if (!value) {
            return of([]);
        }
        const lookups = Array.isArray(value) ? value : [value];
        if (lookups.some((item) => !(item as LookupFieldResult).name)) {
            const lookupGroups: Record<string, string[]> = {};
            lookups.forEach((item) => {
                const lookupType =
                    (item as LookupFieldResult)?.lookupType ??
                    (item as ContactPointLookup)?.type;
                lookupGroups[lookupType] = lookupGroups[lookupType]
                    ? [...lookupGroups[lookupType], item.id]
                    : [item.id];
            });
            const lookupRequests$ = Object.entries(lookupGroups).map(
                ([lookupType, ids]) =>
                    this.filterHttpService
                        .getRecords<LookupFieldResult>(lookupType as any, {}, {
                            expressions: [
                                {
                                    field: 'Id',
                                    operator: FilterOperator.In,
                                    values: ids,
                                },
                            ],
                        } as Query)
                        .pipe(
                            map(
                                (result) =>
                                    result?.results.map((item) => ({
                                        ...item,
                                        lookupType,
                                    })) ?? [],
                            ),
                        ),
            );
            return forkJoin(lookupRequests$).pipe(
                map((groupedResults) => {
                    const results = groupedResults.flatMap((x) => x);

                    const mergedValues = lookups.map((item) => {
                        const lookupType =
                            (item as LookupFieldResult)?.lookupType ??
                            (item as ContactPointLookup)?.type;
                        return (
                            results.find(
                                (result) =>
                                    result.lookupType === lookupType &&
                                    result.id === item.id,
                            ) ?? item
                        );
                    });
                    return mergedValues as LookupFieldResult[];
                }),
            );
        }
        return of(lookups);
    }

    convertLookupToArray(
        lookupResults:
            | LookupFieldResult
            | ContactPointLookup
            | Array<LookupFieldResult | ContactPointLookup>,
    ) {
        return (
            Array.isArray(lookupResults) ? lookupResults : [lookupResults]
        ).filter((arrayValue) => Boolean(arrayValue));
    }

    convertQueryToLookupArray(
        lookupType: LookupSourceType,
        lookupResults:
            | LookupFieldResult
            | ContactPointLookup
            | Array<LookupFieldResult | ContactPointLookup>,
    ): LookupObject[] {
        if (!lookupType) {
            return [];
        }
        return this.convertLookupToArray(lookupResults).map((lookup) => {
            const { id } = lookup;
            return {
                id,
                lookupType,
            };
        });
    }

    lookupResultsAsInfoCardData(
        lookupResults:
            | LookupFieldResult
            | ContactPointLookup
            | Array<LookupFieldResult | ContactPointLookup>,
    ): InfoCardData[] {
        const lookupArray = this.convertLookupToArray(lookupResults);
        return lookupArray?.map((lookupResult) => {
            // doing this as responses from Emails are not LookupFieldResult but some type of ContactPoint
            const contactPoint = lookupResult as ContactPointLookup;
            const hasParty = !!contactPoint.party;

            const result = hasParty
                ? {
                      ...contactPoint.party,
                      name: contactPoint.name,
                      description: contactPoint.party?.name,
                  }
                : (lookupResult as LookupFieldResult);

            const tertiaryInfo = [
                result.description,
                result.partyType,
                result.lookupType,
            ]
                .filter((info) => Boolean(info))
                .filter((info, index, arr) => arr.indexOf(info) === index);

            return {
                primaryInfo: result.name,
                secondaryInfo: { label: result.primaryIdentity ?? result.code },
                tertiaryInfo: tertiaryInfo?.length
                    ? tertiaryInfo.map((info) => ({ label: info }))
                    : null,
                avatars: [new PartyToAvatarPipe().transform(result)],
                id: result.id,
                data: result,
            };
        });
    }
}
